import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Send, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface Message {
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

interface Conversation {
  readonly conversationId: string;
  readonly messages?: Message[];
  readonly extractedConfig?: Record<string, unknown>;
  readonly status?: string;
}

interface ChatResponse {
  readonly sessionId: string;
  readonly message: string;
  readonly extractedConfig: Record<string, unknown>;
}

const PROGRESS_FIELDS = [
  { key: 'facilityType', label: 'Facility type' },
  { key: 'orgSize', label: 'Organization size' },
  { key: 'states', label: 'States of operation' },
  { key: 'regulatoryFrameworks', label: 'Regulatory frameworks' },
  { key: 'accreditationBody', label: 'Accreditation body' },
  { key: 'payerMix', label: 'Payer mix' },
  { key: 'serviceLines', label: 'Service lines' },
  { key: 'ehrSystem', label: 'EHR system' },
  { key: 'documentSystem', label: 'Document system' },
  { key: 'approvalChain', label: 'Approval chain' },
];

export default function OnboardingChatPage() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [extractedConfig, setExtractedConfig] = useState<Record<string, unknown>>({});
  const [completed, setCompleted] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: session } = useQuery({
    queryKey: ['onboarding-session'],
    queryFn: () => api.onboarding.session(),
    retry: false,
  });

  useEffect(() => {
    const conv = (session as { conversation?: Conversation } | undefined)?.conversation;
    if (conv) {
      setMessages(conv.messages ?? []);
      setSessionId(conv.conversationId);
      setExtractedConfig(conv.extractedConfig ?? {});
      if (conv.status === 'completed') setCompleted(true);
    }
  }, [session]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const chatMutation = useMutation({
    mutationFn: async (message: string): Promise<ChatResponse> => {
      const res = await api.onboarding.chat(message, sessionId ?? undefined);
      return res as unknown as ChatResponse;
    },
    onSuccess: (data: ChatResponse) => {
      setSessionId(data.sessionId);
      setMessages((prev) => [...prev, {
        role: 'assistant', content: data.message, timestamp: new Date().toISOString(),
      }]);
      if (data.extractedConfig) setExtractedConfig((prev) => ({ ...prev, ...data.extractedConfig }));
    },
    onError: () => toast.error('Failed to send message'),
  });

  const completeMutation = useMutation({
    mutationFn: () => api.onboarding.complete(),
    onSuccess: () => { setCompleted(true); toast.success('Onboarding complete! Your setup is being reviewed.'); },
  });

  const handleSend = () => {
    const msg = input.trim();
    if (!msg || chatMutation.isPending) return;
    setMessages((prev) => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setInput('');
    chatMutation.mutate(msg);
  };

  const configuredCount = PROGRESS_FIELDS.filter((f) => {
    const v = extractedConfig[f.key];
    return v != null && (!Array.isArray(v) || v.length > 0);
  }).length;

  if (completed) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-gray-800 rounded-xl p-8 max-w-lg text-center space-y-4">
          <CheckCircle2 className="w-16 h-16 text-teal-500 mx-auto" />
          <h2 className="text-xl font-bold text-white">Setup Complete!</h2>
          <p className="text-gray-400">Your configuration is being reviewed by the NOETED team. You'll receive an email when your environment is ready.</p>
          <div className="bg-gray-900 rounded-lg p-4 text-left space-y-2">
            {PROGRESS_FIELDS.map((f) => {
              const v = extractedConfig[f.key];
              return v ? (
                <div key={f.key} className="flex justify-between text-sm">
                  <span className="text-gray-400">{f.label}</span>
                  <span className="text-white">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                </div>
              ) : null;
            })}
          </div>
          <a href="/dashboard" className="inline-block px-6 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded font-medium text-sm">Go to Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Progress sidebar */}
      <div className="w-72 border-r border-gray-700 p-4 space-y-4 hidden md:block">
        <div>
          <h3 className="text-sm font-medium text-white mb-1">Setup Progress</h3>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${(configuredCount / PROGRESS_FIELDS.length) * 100}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-1">{configuredCount} of {PROGRESS_FIELDS.length} configured</p>
        </div>
        <div className="space-y-2">
          {PROGRESS_FIELDS.map((f) => {
            const v = extractedConfig[f.key];
            const done = v != null && (!Array.isArray(v) || v.length > 0);
            return (
              <div key={f.key} className="flex items-center gap-2 text-sm">
                {done ? <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" /> : <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />}
                <span className={done ? 'text-white' : 'text-gray-500'}>{f.label}</span>
              </div>
            );
          })}
        </div>
        {configuredCount >= 5 && (
          <button onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending}
            className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm font-medium">
            Complete Setup
          </button>
        )}
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !chatMutation.isPending && (
            <div className="text-center text-gray-500 mt-20">
              <p className="text-lg">Welcome to NOETED Setup</p>
              <p className="text-sm mt-2">Send a message to start configuring your compliance environment</p>
              <button onClick={() => chatMutation.mutate('Hello, let\'s get started!')}
                className="mt-4 px-6 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">
                Start Setup Interview
              </button>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-lg px-4 py-3 text-sm ${
                msg.role === 'user'
                  ? 'bg-gray-700 text-white'
                  : 'bg-teal-900/30 border border-teal-800/50 text-gray-200'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}

          {chatMutation.isPending && (
            <div className="flex justify-start">
              <div className="bg-teal-900/30 border border-teal-800/50 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-gray-700 p-4">
          <div className="flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type your response..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-teal-500" />
            <button onClick={handleSend} disabled={!input.trim() || chatMutation.isPending}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg disabled:opacity-50"
              aria-label="Send message">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
